/**
 * AtlasVRMenuModule — the in-headset UI for WebXR sessions (Quest 3 / Quest Pro / Vision Pro), rebuilt for the XR P2
 * "VR UI / UX" slice (2026-09-28). DOM never renders inside an immersive session, so everything here is scene
 * geometry: canvas-textured quads, rays and an orb.
 *
 *   The WORLD PANEL (modules/vr/VrWorldPanel) — nine pages: Main · My Worlds · HUD · Comfort · Perf · Chat ·
 *     Inventory · Map · Dialogue. WORLD-ANCHORED: it opens 1.35 m ahead, a little below the eye, facing you, and
 *     then stays where it was put in the WORLD (a scene child at absolute coordinates — THE FRAME LAW: the old panel
 *     set a render-space point as a scene child's position and was drawn an origin away once the FP64 rebase moved).
 *   The WRIST MENU (modules/vr/VrWristMenu) — on the MOVE hand: look at the back of your wrist; the combat strip
 *     (health, weapon, ammo) and eight buttons (Map, Items, Chat, Talk, Comfort, Perf, Panel, Close).
 *   The SUMMON ORB (modules/vr/VrSummonOrb) — for sessions with no X button (hands, Vision Pro).
 *   The POINTERS (modules/vr/VrPointers) — controllers, tracked hands and transient pointers, four slots.
 *
 * Opening: the X button (left controller) toggles the panel (and re-summons it in front when it was left behind);
 * the wrist menu opens by the watch check; the orb opens the panel on a select. Every click is a session `select`
 * (trigger, pinch, gaze-and-pinch) or, with tracked hands, a fingertip poke on the wrist menu.
 *
 * DESKTOP IS SACRED: the system returns at once unless a session is presenting; nothing here writes graphics
 * settings. The comfort page only WRITES lib/xr_comfort through `setComfort` — the locomotion slice owns every
 * behaviour. The probe lever is `window.__vrUi`.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { XRModule } from 'enginex/xr/index.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { VrPointers, type Pointer, type PickHit, type PointerHost, type XrSlots } from './vr/VrPointers.js';
import { VrWorldPanel, type NpcTalkLike, type LocalMapLike } from './vr/VrWorldPanel.js';
import { VrWristMenu, type WristData } from './vr/VrWristMenu.js';
import { VrSummonOrb } from './vr/VrSummonOrb.js';
import * as data from './vr/vr_data.js';
import { comfort, onComfortChange } from '../lib/xr_comfort.js';
import { ammoReadout } from '../lib/xr_ui_data.js';
import { WRIST_ITEMS, wristTarget, isPageId, uvToCanvas, type PageId, type WristItemId } from '../lib/xr_ui_layout.js';
import { readWrist, readWristJoint, rotate, headingOf, add, scale, sub, len, dot, norm, type V3, type Q4 } from '../lib/xr_ui_input.js';

/** A world panel left farther than this is out of reach; X re-summons it in front instead of closing it. */
const RESUMMON_M = 2.5;
/** Walk this far from the panel and it closes on its own. */
const ABANDON_M = 12;

export class AtlasVRMenuModule implements IModule, PointerHost {
  readonly name = 'AtlasVRMenu';

  private app!: App;
  private scene!: THREE.Scene;
  private panel!: VrWorldPanel;
  private readonly wrist = new VrWristMenu();
  private readonly orb = new VrSummonOrb();
  private readonly pointers = new VrPointers(this);
  private rig: THREE.Object3D | null = null;
  private presenting = false;
  private prevX = false;
  private readingFrom: 'grip' | 'joint' | null = null;
  private hasX = false;
  private worldAt: bigint | null = null;
  private head: V3 = { x: 0, y: 1.6, z: 0 };
  private headFwd: V3 = { x: 0, y: 0, z: -1 };
  private pokeHover: string | null = null;
  private wristData: WristData | null = null;
  private wristDataT = 0;
  private lastClick: { target: string; kind: string; slot: number; at: number } | null = null;
  private offComfort: (() => void) | null = null;
  // rig-local ↔ scene-local (absolute) — refreshed each poll.
  private readonly mRigToScene = new THREE.Matrix4();
  private readonly mSceneToRig = new THREE.Matrix4();
  private readonly qRigToScene = new THREE.Quaternion();
  private readonly tv = new THREE.Vector3();
  private readonly tv2 = new THREE.Vector3();

  constructor(private readonly xr: XRModule) {}

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    this.panel = new VrWorldPanel({
      npc: () => app.getModule<NpcTalkLike & IModule>('NpcInteract') ?? null,
      localMap: () => app.getModule<LocalMapLike & IModule>('AtlasLocalMap') ?? null,
      enterWorld: (worldId) => { bus.emit('atlas:enter-world', { worldId }); this.panel.hide(); },
      exitVr: () => { void this.xr.endSession(); },
      recenter: () => this.showPanel(),
      headHeading: () => this.headHeadingWorld(),
      wristHand: () => comfort().moveHand,
      hasXButton: () => this.hasX,
    });
    bus.on('atlas:status', ({ text }) => this.panel.setStatus(text));
    const xrm = app.renderer.native.xr;
    // THE FOUR-SLOT LAW: claim every controller slot before any session so a transient pointer is never ignored.
    this.pointers.claim(xrm as unknown as XrSlots);
    xrm.addEventListener('sessionstart', () => this.onStart());
    xrm.addEventListener('sessionend', () => this.onEnd());
    this.offComfort = onComfortChange(() => { this.panel.invalidate(); this.wrist.surface.invalidate(); });
    app.world.addSystem(makeSystem('AtlasVRMenu', Phase.Input, 26, (dt) => this.poll(dt)));
    this.installLever();
  }

  // ─── Session ──────────────────────────────────────────────────────────────────

  private onStart(): void {
    this.presenting = true;
    this.prevX = false;
    this.panel.hide();
    this.wrist.close('session start');
  }

  private onEnd(): void {
    this.presenting = false;
    this.panel.hide();
    this.wrist.close('session end');
    this.orb.update(0, this.head, this.headFwd, false, false);
    this.pointers.hideVisuals();
  }

  // ─── Frames: rig-local (controllers, head, wrist, orb) ↔ scene-local (the world-anchored panel) ────────────────

  private refreshFrames(rig: THREE.Object3D): void {
    // Space campaign L (2026-10-01): in the sector the world rushes past at hundreds of m/s — a world-anchored panel is
    // left behind (and closed by ABANDON_M) in its first frame. There the panel rides the COCKPIT: a rig child, and the
    // rig is "the scene" for every conversion below (identity). Leaving the sector is a world hop — the panel closes.
    const anchor: THREE.Object3D = atlas.spaceFlight ? rig : this.scene;
    if (this.panel.surface.mesh.parent !== anchor) anchor.add(this.panel.surface.mesh);
    if (anchor === rig) {
      this.mSceneToRig.identity(); this.mRigToScene.identity();
      this.mRigToScene.decompose(this.tv, this.qRigToScene, this.tv2);
      return;
    }
    rig.updateWorldMatrix(true, false);
    this.mSceneToRig.copy(rig.matrixWorld).invert().multiply(this.scene.matrixWorld);
    this.mRigToScene.copy(this.mSceneToRig).invert();
    this.mRigToScene.decompose(this.tv, this.qRigToScene, this.tv2);
  }
  private toScene(v: V3): V3 { const p = this.tv.set(v.x, v.y, v.z).applyMatrix4(this.mRigToScene); return { x: p.x, y: p.y, z: p.z }; }
  private dirToScene(d: V3): V3 { const p = this.tv.set(d.x, d.y, d.z).applyQuaternion(this.qRigToScene); return { x: p.x, y: p.y, z: p.z }; }
  private toRig(v: V3): V3 { const p = this.tv.set(v.x, v.y, v.z).applyMatrix4(this.mSceneToRig); return { x: p.x, y: p.y, z: p.z }; }

  /** The head's WORLD heading (the map arrow). */
  private headHeadingWorld(): number { return headingOf(this.dirToScene(this.headFwd)); }

  private attach(rig: THREE.Object3D): void {
    if (rig === this.rig) return;
    this.pointers.attach(rig);
    rig.add(this.wrist.surface.mesh, this.orb.group);
    if (this.panel.surface.mesh.parent !== this.scene) this.scene.add(this.panel.surface.mesh);
    this.rig = rig;
  }

  // ─── Per frame ────────────────────────────────────────────────────────────────

  private poll(dt: number): void {
    const xrm = this.app.renderer.native.xr;
    if (!xrm.isPresenting) { if (this.presenting) this.onEnd(); return; }
    const session = xrm.getSession();
    const rig = atlas.localMesh;
    const cam = rig?.children[0];
    if (!session || !rig || !cam) return;
    this.presenting = true;
    this.attach(rig);
    this.refreshFrames(rig);
    // three writes the XR head pose into the rig-child camera (rig-local); last frame's is fine for UI placement.
    const q: Q4 = { x: cam.quaternion.x, y: cam.quaternion.y, z: cam.quaternion.z, w: cam.quaternion.w };
    this.head = { x: cam.position.x, y: cam.position.y, z: cam.position.z };
    this.headFwd = rotate({ x: 0, y: 0, z: -1 }, q);

    // A world hop closes everything (the panel's world is gone).
    if (atlas.currentWorldId !== this.worldAt) {
      this.worldAt = atlas.currentWorldId;
      this.panel.hide();
      this.wrist.close('world changed');
    }

    // The X button (left controller, xr-standard buttons[4]) — and whether the session HAS one (else the orb).
    let x = false;
    let hasX = false;
    for (const src of session.inputSources) {
      const gp = src.gamepad;
      if (src.handedness === 'left' && gp && gp.buttons.length > 4) { hasX = true; x = gp.buttons[4]?.pressed ?? false; }
    }
    if (hasX !== this.hasX) { this.hasX = hasX; this.panel.invalidate(); }
    if (x && !this.prevX) this.togglePanel();
    this.prevX = x;

    // A panel left far behind closes on its own.
    if (this.panel.open && this.panelDistance() > ABANDON_M) this.panel.hide();

    // The wrist menu on the MOVE hand.
    const hand = comfort().moveHand;
    if (hand !== this.wrist.hand) { this.wrist.close('hand changed'); this.wrist.hand = hand; }
    const grip = this.pointers.gripOf(hand);
    const joint = grip ? null : this.pointers.wristOf(hand);
    const reading = grip ? readWrist(grip.pos, grip.rot, hand, this.head, this.headFwd)
      : joint ? readWristJoint(joint.pos, joint.rot, this.head, this.headFwd) : null;
    const readingFrom = grip ? 'grip' : joint ? 'joint' : null;
    if (readingFrom !== this.readingFrom) { this.readingFrom = readingFrom; this.wrist.resetGate(); }
    const prevHovers = this.pointers.hovers();
    let wristHovered = this.pokeHover !== null;
    for (const h of prevHovers) if (h.startsWith('wrist:')) wristHovered = true;
    this.wrist.update(dt, reading, this.head, this.headFwd, wristHovered);

    // The orb — only when there is nothing to press.
    this.orb.update(dt, this.head, this.headFwd, !this.hasX && !this.panel.open && !this.wrist.open, prevHovers.has('orb:'));

    // Pointers: hover, the drawn rays (up with a surface, or while a ray is on the orb).
    const surfaceUp = this.panel.open || this.wrist.open;
    this.pointers.update(dt, (_p, hit) => surfaceUp || (hit?.target === 'orb:'));

    // Pokes: the OTHER hand's fingertip on the wrist menu.
    this.pokeHover = null;
    if (this.wrist.open) {
      for (const p of this.pointers.pointers) {
        if (p.handedness === this.wrist.hand) continue;
        const tip = this.pointers.fingertip(p);
        if (!tip) continue;
        const r = this.wrist.poke(p.slot, tip);
        if (r.hover) this.pokeHover = r.hover;
        if (r.fire) this.clickTarget(`wrist:${r.fire}`, 'poke', p.slot);
      }
    }

    // Paint.
    const hovers = this.pointers.hovers();
    if (this.pokeHover) hovers.add(`wrist:${this.pokeHover}`);
    this.panel.tick(dt, hovers);
    if (this.wrist.open) {
      this.wristDataT -= dt;
      if (!this.wristData || this.wristDataT <= 0) { this.wristDataT = 0.25; this.wristData = this.readWristData(); }
      this.wrist.tick(hovers, this.wristData);
    } else this.wristData = null;
  }

  private readWristData(): WristData {
    const v = data.vitals();
    const eq = data.equipped();
    return { hp: v.hp, maxHp: v.maxHp, weapon: eq.spec.name, ammo: ammoReadout(eq.spec, data.feed(), Date.now()), time: data.timeOfDay() };
  }

  private panelDistance(): number { return len(sub(this.panel.surface.pos(), this.toScene(this.head))); }

  // ─── The panel ────────────────────────────────────────────────────────────────

  /** Open (or move) the world panel in front of the head, in the WORLD frame. */
  private showPanel(page?: PageId): void {
    this.panel.show(this.toScene(this.head), this.dirToScene(this.headFwd), page);
  }

  private openPanel(page?: PageId): void {
    if (!this.panel.open) this.showPanel(page);
    else if (page) this.panel.setPage(page);
  }

  private togglePanel(): void {
    if (!this.panel.open) { this.showPanel(); return; }
    // Left behind or out of view → bring it here; otherwise close it.
    const toPanel = sub(this.panel.surface.pos(), this.toScene(this.head));
    const inView = dot(norm(this.dirToScene(this.headFwd)), norm(toPanel)) > 0.35;
    if (len(toPanel) > RESUMMON_M || !inView) this.showPanel();
    else this.panel.hide();
  }

  // ─── PointerHost ──────────────────────────────────────────────────────────────

  pick(o: V3, d: V3, p: Pointer): PickHit | null {
    let best: PickHit | null = null;
    if (this.panel.open) {
      const h = this.panel.hit(this.toScene(o), this.dirToScene(d)); // rigid transform: t is the same in both frames
      if (h) best = { target: h.target, t: h.t, point: add(o, scale(d, h.t)) };
    }
    // The menu hand cannot aim at the menu it is holding up (its ray grazes the menu plane).
    if (this.wrist.open && !(p.handedness === this.wrist.hand && len(sub(o, this.wrist.surface.pos())) < 0.25)) {
      const h = this.wrist.hit(o, d);
      if (h && (!best || h.t < best.t)) best = h;
    }
    const oh = this.orb.hit(o, d);
    if (oh && (!best || oh.t < best.t)) best = oh;
    return best;
  }

  click(p: Pointer, target: string): void { this.clickTarget(target, p.kind, p.slot); }

  private clickTarget(target: string, kind: string, slot: number): void {
    this.lastClick = { target, kind, slot, at: Date.now() };
    const i = target.indexOf(':');
    const surf = i < 0 ? target : target.slice(0, i);
    const id = i < 0 ? '' : target.slice(i + 1);
    if (surf === 'panel') this.panel.click(id);
    else if (surf === 'wrist') this.clickWrist(id);
    else if (surf === 'orb') this.openPanel();
  }

  private clickWrist(id: string): void {
    const item = id.replace(/^wrist:/, '') as WristItemId;
    if (!WRIST_ITEMS.some((it) => it.id === item)) return;
    const t = wristTarget(item);
    if (t.kind === 'close') { this.wrist.close('closed'); return; }
    if (t.kind === 'talk') {
      const npc = this.app.getModule<NpcTalkLike & IModule>('NpcInteract');
      const talking = !!npc?.xrTalk();
      const ok = talking || (npc?.xrTalkNearest() ?? false);
      this.openPanel('dialogue');
      if (!ok) this.panel.setStatus('No one is close enough to talk to.');
    } else if (t.kind === 'panel') this.openPanel();
    else this.openPanel(t.page);
    this.wrist.close('opened the panel');
  }

  // ─── The probe lever ──────────────────────────────────────────────────────────

  /** A widget's centre in RIG-LOCAL space (where an emulated controller aims): `panel:<id>` / `wrist:<id>` / `orb:`. */
  private aimPoint(target: string): V3 | null {
    if (target === 'orb:') return this.orb.visible ? this.orb.center() : null;
    const i = target.indexOf(':');
    const surf = target.slice(0, i), id = target.slice(i + 1);
    const surface = surf === 'panel' ? this.panel.surface : surf === 'wrist' ? this.wrist.surface : null;
    if (!surface || !surface.visible) return null;
    const snap = (surf === 'panel' ? this.panel.snapshot() : this.wrist.snapshot()) as { widgets: Array<{ id: string; rect: { x: number; y: number; w: number; h: number } }> };
    const base = id.replace(/:(left|mid|right)$/, '');
    const w = snap.widgets.find((x) => x.id === base);
    if (!w) return null;
    const zone = /:(left|right)$/.exec(id)?.[1];
    const px = zone === 'left' ? w.rect.x + 30 : zone === 'right' ? w.rect.x + w.rect.w - 30 : w.rect.x + w.rect.w / 2;
    const py = w.rect.y + w.rect.h / 2;
    const lx = (px / surface.cw - 0.5) * surface.wM, ly = (0.5 - py / surface.ch) * surface.hM;
    const p = add(surface.pos(), rotate({ x: lx, y: ly, z: 0 }, surface.rot()));
    return surf === 'panel' ? this.toRig(p) : p;
  }

  private snapshot(): Record<string, unknown> {
    const rig = this.rig;
    return {
      presenting: this.presenting, hasX: this.hasX, head: this.head, headFwd: this.headFwd,
      rig: rig ? { x: rig.position.x, y: rig.position.y, z: rig.position.z, yaw: rig.rotation.y, name: rig.name } : null,
      panel: this.panel.snapshot(), panelParent: this.panel.surface.mesh.parent === this.scene ? 'scene' : this.panel.surface.mesh.parent?.name ?? null,
      panelDistance: this.presenting ? this.panelDistance() : null,
      wrist: this.wrist.snapshot(), orb: this.orb.snapshot(), pointers: this.pointers.snapshot(),
      pokeHover: this.pokeHover, lastClick: this.lastClick, comfort: comfort(), inHand: data.equipped().spec.key,
    };
  }

  private installLever(): void {
    (window as unknown as Record<string, unknown>).__vrUi = {
      state: () => this.snapshot(),
      open: (page?: string) => { if (page && !isPageId(page)) return false; this.openPanel(page as PageId | undefined); return true; },
      close: () => this.panel.hide(),
      page: (p: string) => { if (isPageId(p)) this.panel.setPage(p); },
      click: (target: string) => this.clickTarget(target, 'lever', -1),
      wristOpen: () => { const g = this.pointers.gripOf(this.wrist.hand) ?? this.pointers.wristOf(this.wrist.hand); const a = g ? g.pos : add(this.head, add(scale(this.headFwd, 0.35), { x: 0, y: -0.25, z: 0 })); this.wrist.openAt(a, this.head); return true; },
      wristClose: () => this.wrist.close('lever'),
      aim: (target: string) => this.aimPoint(target),
      toRig: (v: V3) => this.toRig(v),
      toScene: (v: V3) => this.toScene(v),
      uvToCanvas,
    };
  }

  dispose(): void {
    this.offComfort?.();
    this.panel.dispose();
    this.wrist.dispose();
    this.orb.dispose();
    this.pointers.dispose();
    delete (window as unknown as Record<string, unknown>).__vrUi;
  }
}
